/**
 * Site search with instant results: an ARIA combobox over /api/v1/search. Arrow keys move
 * through results, Enter opens one (or the full results page), Escape closes. "/" anywhere
 * focuses it.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { CornerDownLeft, Search } from 'lucide-react'
import { api, coverUrl, qs } from '../lib/api'
import { recordHref, TYPE_LABELS } from '../lib/format'
import type { SearchHit } from '../lib/types'

export function SearchBox() {
  const navigate = useNavigate()
  const id = useId()
  const input = useRef<HTMLInputElement>(null)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<SearchHit[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !t.closest('input, textarea, select, [contenteditable]')) {
        e.preventDefault()
        input.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) {
      setHits([])
      return
    }
    let cancelled = false
    const t = setTimeout(() => {
      api<{ items: SearchHit[] }>(`/api/v1/search${qs({ q: term, limit: 8 })}`)
        .then((r) => {
          if (!cancelled) {
            setHits(r.items)
            setActive(-1)
          }
        })
        .catch(() => !cancelled && setHits([]))
    }, 150)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [q])

  const go = (path: string) => {
    setOpen(false)
    setQ('')
    input.current?.blur()
    navigate(path)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => Math.min(a + 1, hits.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const hit = hits[active]
      if (hit) go(recordHref(hit.type, hit.id))
      else if (q.trim()) go(`/search${qs({ q: q.trim() })}`)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const listId = `${id}-results`
  const showList = open && q.trim().length >= 2
  return (
    <div className="searchbox" role="search">
      <Search className="searchbox-icon" size={18} aria-hidden="true" />
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-label="Search systems, books, publishers and creators"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${id}-${active}` : undefined}
        placeholder="Search systems, books, people…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        spellCheck={false}
      />
      <kbd className="searchbox-key" aria-hidden="true">
        /
      </kbd>
      {showList && (
        <ul id={listId} role="listbox" className="searchbox-results">
          {hits.map((h, i) => (
            <li
              key={`${h.type}:${h.id}`}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === active}
              className="searchbox-hit"
              onMouseDown={(e) => {
                e.preventDefault()
                go(recordHref(h.type, h.id))
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="searchbox-thumb" aria-hidden="true">
                {h.cover ? <img src={coverUrl(h.cover)!} alt="" /> : null}
              </span>
              <span className="searchbox-name">{h.name}</span>
              <span className="searchbox-type">{TYPE_LABELS[h.type].one}</span>
            </li>
          ))}
          <li
            id={`${id}-all`}
            role="option"
            aria-selected={false}
            className="searchbox-all"
            onMouseDown={(e) => {
              e.preventDefault()
              go(`/search${qs({ q: q.trim() })}`)
            }}
          >
            {hits.length ? 'See all results' : 'No quick matches. Search everything'} for “{q.trim()}”
            <CornerDownLeft size={14} aria-hidden="true" />
          </li>
        </ul>
      )}
    </div>
  )
}
